import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RefreshCw, TrendingUp, MessageCircle, FileText, CheckCircle, AlertTriangle, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminNav from './AdminNav';

const AdminAnalytics = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, logout } = useAuth();
  const [chatbots, setChatbots] = useState([]);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [chatbotId, setChatbotId] = useState('');
  const [granularity, setGranularity] = useState('day');

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    fetchChatbots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, chatbotId, granularity]);

  const fetchChatbots = async () => {
    try {
      const response = await fetch('/api/admin/chatbots', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok) {
        setChatbots([]);
        return;
      }

      const data = await response.json();
      const chatbotsArray = Array.isArray(data) ? data : (data.chatbots || []);
      setChatbots(chatbotsArray);
    } catch (err) {
      console.error('Error fetching chatbots:', err);
      setChatbots([]);
    }
  };

  const buildQueryParams = (endpoint) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (chatbotId) params.set('chatbotId', chatbotId);
    if (endpoint === 'trends') params.set('granularity', granularity);
    return params.toString();
  };

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError('');

    try {
      const [overviewRes, trendsRes, breakdownRes] = await Promise.all([
        fetch(`/api/admin/analytics/overview?${buildQueryParams('overview')}`, {
          headers: getAuthHeaders()
        }),
        fetch(`/api/admin/analytics/trends?${buildQueryParams('trends')}`, {
          headers: getAuthHeaders()
        }),
        fetch(`/api/admin/analytics/by-chatbot?${buildQueryParams('breakdown')}`, {
          headers: getAuthHeaders()
        })
      ]);

      if (overviewRes.status === 401 || trendsRes.status === 401 || breakdownRes.status === 401) {
        logout();
        return;
      }

      const [overviewData, trendsData, breakdownData] = await Promise.all([
        overviewRes.json(),
        trendsRes.json(),
        breakdownRes.json()
      ]);

      if (!overviewRes.ok) {
        throw new Error(overviewData.error || t('admin.analytics.failedToLoadOverview'));
      }
      if (!trendsRes.ok) {
        throw new Error(trendsData.error || t('admin.analytics.failedToLoadTrends'));
      }
      if (!breakdownRes.ok) {
        throw new Error(breakdownData.error || t('admin.analytics.failedToLoadBreakdown'));
      }

      setOverview(overviewData);
      setTrends(Array.isArray(trendsData.trends) ? trendsData.trends : []);
      setBreakdown(Array.isArray(breakdownData.breakdown) ? breakdownData.breakdown : []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message || t('admin.analytics.failedToLoadAnalytics'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.analytics.title')}</h1>
              <p className="text-gray-600 mt-2">{t('admin.analytics.subtitle')}</p>
            </div>
            <button
              onClick={fetchAnalytics}
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              {isLoading ? t('admin.analytics.loading') : t('common.refresh')}
            </button>
          </div>
        </div>

        <AdminNav />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.analytics.startDate')}</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.analytics.endDate')}</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.analytics.chatbot')}</label>
              <select
                value={chatbotId}
                onChange={(e) => setChatbotId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('admin.analytics.allChatbots')}</option>
                {chatbots.map((cb) => (
                  <option key={cb.id} value={cb.id}>
                    {cb.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.analytics.granularity')}</label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="day">{t('admin.analytics.daily')}</option>
                <option value="week">{t('admin.analytics.weekly')}</option>
                <option value="month">{t('admin.analytics.monthly')}</option>
              </select>
            </div>
          </div>
        </div>

        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('admin.analytics.totalConversations')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{overview.totalConversations || 0}</p>
                </div>
                <MessageCircle className="text-blue-500" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('admin.analytics.totalOffers')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{overview.totalOffers || 0}</p>
                </div>
                <FileText className="text-green-500" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('admin.analytics.offersAccepted')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{overview.acceptedOffers || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('admin.analytics.acceptanceRate', { rate: overview.acceptanceRate || 0 })}</p>
                </div>
                <CheckCircle className="text-green-600" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('admin.analytics.errorRate')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{overview.errorRate || 0}%</p>
                  <p className="text-xs text-gray-500 mt-1">{t('admin.analytics.errorsCount', { count: overview.errorCount || 0 })}</p>
                </div>
                <AlertTriangle className="text-red-500" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('admin.analytics.avgMessagesPerConversation')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{overview.avgMessagesPerConversation || 0}</p>
                </div>
                <TrendingUp className="text-purple-500" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('admin.analytics.totalRevenue')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {overview.totalRevenue ? `€${parseFloat(overview.totalRevenue).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '€0.00'}
                  </p>
                </div>
                <DollarSign className="text-yellow-500" size={32} />
              </div>
            </div>
          </div>
        )}

        {trends.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.analytics.trends')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="conversations" stroke="#3b82f6" name={t('admin.analytics.conversations')} />
                <Line type="monotone" dataKey="offers" stroke="#10b981" name={t('admin.analytics.offers')} />
                <Line type="monotone" dataKey="accepted" stroke="#059669" name={t('admin.analytics.accepted')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {breakdown.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.analytics.perChatbotBreakdown')}</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.analytics.chatbot')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.analytics.conversations')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.analytics.offers')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.analytics.accepted')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.analytics.acceptanceRateCol')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {breakdown.map((item) => (
                    <tr key={item.chatbotId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.chatbotName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.conversations}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.offers}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.accepted}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.acceptanceRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isLoading && !overview && !error && (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <p className="text-gray-500">{t('admin.analytics.noDataAvailable')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
